import * as React from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
    type Registration as RegistrationType,
    type Seeking,
    type Education,
} from '../../context/AppStateProvider/types';
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppState, useAppDispatchActions } from '../../context/AppStateProvider/useAppState';
import { useApi } from '../../context/ApiProvider/useApi';
import { wordpressFetch } from '../../utils/wordpress';
import { ActionButton } from '../Button/Button';

type View = 'login' | 'register' | 'forgotPassword';

type ApiError = {
    code: string;
    message: string;
    data: {
        status: number;
        errors: Record<string, string[]>;
    };
};

const SEEKING_OPTIONS: { value: Seeking; label: string }[] = [
    {
        value: 'Domestic undergraduate student',
        label: 'Domestic undergraduate student',
    },
    {
        value: 'Domestic postgraduate student',
        label: 'Domestic postgraduate student',
    },
    {
        value: 'International undergraduate student',
        label: 'International undergraduate student',
    },
    {
        value: 'International postgraduate student',
        label: 'International postgraduate student',
    },
    {
        value: 'Parent or guardian',
        label: 'Parent or guardian',
    },
    { value: 'School staff', label: 'School staff' },
    { value: 'Agent', label: 'Agent' },
];

const EDUCATION_OPTIONS: Education[] = [
    'Year 9 student',
    'Year 10 student',
    'Year 11 student',
    'Year 12 student',
    'High school graduate',
    'University or TAFE student',
    'Working or career break',
];

function Login({ setView, handleClose }: { setView: (view: View) => void; handleClose: () => void }) {
    const [errors, setErrors] = React.useState<Record<string, string[]>>({});
    const [formError, setFormError] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState(false);
    const { userMutate } = useApi();
    const formRef = React.useRef<HTMLFormElement>(null);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const form = event.currentTarget;
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        setErrors({});
        setFormError('');
        setIsLoading(true);

        const formData = new FormData(event.currentTarget);
        const data = Object.fromEntries(formData.entries());

        try {
            const response = await wordpressFetch({
                path: '/wp-json/open-day-26/v1/account/login',
                method: 'POST',
                parse: true,
                data,
            });

            // --- GTM TRACKING START ---
            (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];

            (window as any).dataCoreLayer.push({
                'event': 'od_form_submit',
                'form_id': 'login-form',
                'gtm.formSubmit': true,
                'gtm.elementId': 'login-form',
                'gtm.elementClasses': formRef.current?.className || '',
                'gtm.elementUrl': window.location.href,
                'gtm.elementTarget': '',
            });
            // --- GTM TRACKING END ---

            // a new nonce will be issued after login make sure its reset
            if ((response as any).nonce) {
                const newNonce = (response as any).nonce;
                (window as any).openDayMapData ??= {};
                (window as any).openDayMapData.nonce = newNonce;
            }

            userMutate();
            handleClose();
        } catch (error) {
            console.log(error);

            const apiError = error as ApiError;
            setErrors(apiError.data.errors);
            if (apiError?.message) {
                setFormError(apiError.message || 'An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className='mb-10 inline-flex flex-col items-start justify-start gap-1.5 self-stretch'>
                <div className='inline-flex items-center justify-center gap-[3.14px] bg-[var(--colour-gold)] px-3 py-1'>
                    <div className="justify-center font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase">
                        explore your{' '}
                    </div>
                </div>
                <div className='inline-flex items-center justify-center gap-[3.14px] bg-[var(--colour-gold)] px-3 py-1'>
                    <div className='justify-center'>
                        <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase">
                            uni
                        </span>
                        <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase italic">
                            verse
                        </span>
                        <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase">
                            {' '}
                            of possibility
                        </span>
                    </div>
                </div>
            </div>
            <form
                onSubmit={handleSubmit}
                id='login-form'
                ref={formRef}
                noValidate>
                <Stack spacing={2}>
                    {formError && <Alert severity='error'>{formError}</Alert>}
                    <TextField
                        required
                        autoFocus
                        margin='dense'
                        id='email'
                        name='email'
                        label='Email Address'
                        type='email'
                        fullWidth
                        variant='outlined'
                        autoComplete='email'
                        error={!!errors.email}
                        helperText={errors.email?.[0]}
                        color='secondary'
                    />
                    <TextField
                        required
                        margin='dense'
                        id='password'
                        name='password'
                        label='Password'
                        type='password'
                        fullWidth
                        variant='outlined'
                        autoComplete='current-password'
                        error={!!errors.password}
                        helperText={errors.password?.[0]}
                        color='secondary'
                    />
                    <ActionButton
                        onClick={(event) => {
                            event.preventDefault();
                            formRef.current?.requestSubmit();
                        }}
                        variant='light'
                        fullWidth={true}
                        disabled={isLoading}>
                        {isLoading ? (
                            <CircularProgress
                                size={24}
                                color='inherit'
                            />
                        ) : (
                            'Sign in'
                        )}
                    </ActionButton>
                    <Stack>
                        <div className='flex w-full justify-between gap-3 rounded-lg py-2'>
                            <div>
                                <span className="font-['Effra'] text-base leading-5 font-normal">
                                    Don't have an account?{' '}
                                </span>
                                <button
                                    className="cursor-pointer font-['Effra'] text-base leading-5 font-normal underline"
                                    onClick={() => setView('register')}>
                                    Register for free
                                </button>
                            </div>
                            <button
                                className="cursor-pointer font-['Effra'] text-base leading-5 font-normal underline"
                                onClick={() => setView('forgotPassword')}>
                                Forgot password?
                            </button>
                        </div>
                    </Stack>
                </Stack>
            </form>
        </>
    );
}

const ArrowIcon = () => (
    <svg
        width='19'
        height='19'
        viewBox='0 0 19 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'>
        <path
            d='M8.92871 0.337402L18.0859 9.0376L18.2119 9.15771L18.085 9.27783L8.92871 17.9653L8.81445 18.0737L8.7002 17.9644L7.76172 17.064L7.63672 16.9438L7.7627 16.8247L15.0078 9.94482H0.165039V8.34521H15.0068L7.7627 1.47803L7.63672 1.35889L7.76172 1.23877L8.7002 0.338379L8.81445 0.229004L8.92871 0.337402Z'
            fill='currentColor'
        />
    </svg>
);

function Success({ handleClose }: { handleClose: () => void }) {
    return (
        <>
            <div className='mb-4 inline-flex items-center justify-center gap-[3.14px] bg-[var(--colour-gold)] px-3 py-1'>
                <div className='justify-center'>
                    <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase">
                        See You There!
                    </span>
                </div>
            </div>
            <p className="mt-3 mb-10 font-['Effra'] text-lg leading-5 text-[var(--colour-white)]">
                You are now registered for Open Day and are in the draw to win an iPhone 17, MacBook Air 13 and other amazing prizes
            </p>
            <div className="inline-flex items-center justify-center gap-5 rounded-lg px-5 py-3.5 outline outline-2 transition-all cursor-pointer active:scale-[0.98] hover:opacity-90 w-full bg-white outline-[var(--colour-charcoal-dark)] text-[var(--colour-charcoal-dark)] ">
                <a href="#open-day-map" onClick={() => {handleClose()}} className="registration-success-modal font-['Effra'] text-xl leading-7 font-bold uppercase">
                    View What's on
                </a>
                <div className='flex items-center justify-start'>
                    <div className='inline-flex items-center justify-center rounded-[36.72px] bg-[var(--colour-gold)] p-2.5 text-[#0B0B0B]'>
                        <ArrowIcon />
                    </div>
                </div>
            </div>
        </>
    );
}

function Register({ setView, handleClose }: { setView: (view: View) => void; handleClose: () => void }) {
    const [errors, setErrors] = React.useState<Record<string, string[]>>({});
    const [formError, setFormError] = React.useState<string>('');
    const [seekingValue, setSeekingValue] = React.useState<Seeking | ''>('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const { userMutate } = useApi();
    const formRef = React.useRef<HTMLFormElement>(null);

    // Supplied by `open-day-map` block template (ACF field injection).
    const openDayTermsUrl =
        (window as any)?.openDayMapData?.competition_terms_and_conditions ?? '';

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const form = event.currentTarget;
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        setErrors({});
        setFormError('');
        setIsLoading(true);

        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries(formData.entries());
        const data: Partial<RegistrationType> = {
            ...formJson,
            subscribe: formJson.subscribe === 'on',
        };

        try {
            const response = await wordpressFetch({
                path: '/wp-json/open-day-26/v1/account/register',
                method: 'POST',
                data,
            });

            // --- GTM TRACKING START ---
            (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];

            (window as any).dataCoreLayer.push({
                'event': 'od_form_submit',
                'form_id': 'register-form',
                'gtm.formSubmit': true,
                'gtm.elementId': 'register-form',
                'gtm.elementClasses': formRef.current?.className || '',
                'gtm.elementUrl': window.location.href,
                'gtm.elementTarget': '',
            });
            // --- GTM TRACKING END ---

            // a new nonce will be issued after login make sure its reset
            if ((response as any).nonce) {
                const newNonce = (response as any).nonce;
                (window as any).openDayMapData ??= {};
                (window as any).openDayMapData.nonce = newNonce;
            }

            // On success, automatically log the user in or switch to login view
            userMutate();
            //handleClose();
            setSuccess(true);
        } catch (error) {
            const apiError = error as ApiError;

            setErrors(apiError.data.errors ?? []);
            if (apiError?.message) {
                setFormError(apiError.message || 'An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const educationLabel =
        seekingValue === 'Parent or guardian'
            ? "What best describes your child's current level of education?"
            : 'What best describes your current level of education?';

    const filteredEducationOptions = React.useMemo(() => {
        if (seekingValue === 'Parent or guardian') {
            return EDUCATION_OPTIONS.filter(
                (option) =>
                    option.includes('Year 9') ||
                    option.includes('Year 10') ||
                    option.includes('Year 11') ||
                    option.includes('Year 12'),
            );
        }
        return EDUCATION_OPTIONS;
    }, [seekingValue]);

    return (
        <>
            {success ? (
                <Success handleClose={handleClose} />
            ) : (
                <>
                    <div className='mb-4 inline-flex items-center justify-center gap-[3.14px] bg-[var(--colour-gold)] px-3 py-1'>
                        <div className='justify-center'>
                            <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase">
                                Create YOUR CURTIN uni
                            </span>
                            <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase italic">
                                verse
                            </span>
                        </div>
                    </div>
                    <div className="mb-10 justify-center self-stretch font-['Effra'] text-lg leading-5 font-normal text-[var(--colour-white)]">
                        Sign up to access the Open Day event planner and collate and save the activities/sessions you’re
                        interested in.{' '}
                    </div>
                    <form
                        onSubmit={handleSubmit}
                        id='register-form'
                        ref={formRef}
                        noValidate>
                        <Stack spacing={2}>
                            {formError && <Alert severity='error'>{formError}</Alert>}
                            <TextField
                                required
                                autoFocus
                                margin='dense'
                                id='first_name'
                                name='first_name'
                                label='First name'
                                type='text'
                                fullWidth
                                variant='outlined'
                                autoComplete='given-name'
                                error={!!errors.first_name}
                                helperText={errors.first_name?.[0]}
                                color='secondary'
                            />
                            <TextField
                                required
                                margin='dense'
                                id='email'
                                name='email'
                                label='Email address'
                                type='email'
                                fullWidth
                                variant='outlined'
                                autoComplete='email'
                                error={!!errors.email}
                                helperText={errors.email?.[0]}
                                color='secondary'
                            />
                            <TextField
                                required
                                margin='dense'
                                id='phone'
                                name='phone'
                                label='Mobile number'
                                type='tel'
                                fullWidth
                                variant='outlined'
                                autoComplete='tel-national'
                                error={!!errors.phone}
                                helperText={errors.phone?.[0]}
                                color='secondary'
                            />
                            <FormControl
                                fullWidth
                                required
                                error={!!errors.seeking}>
                                <InputLabel id='seeking-label'>What best describes you...</InputLabel>
                                <Select
                                    labelId='seeking-label'
                                    id='seeking'
                                    name='seeking'
                                    label='What best describes you...'
                                    value={seekingValue}
                                    color='secondary'
                                    onChange={(e) => setSeekingValue(e.target.value as Seeking)}>
                                    {SEEKING_OPTIONS.map((option) => (
                                        <MenuItem
                                            className='text-2xl'
                                            key={option.value}
                                            value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.seeking && <FormHelperText>{errors.seeking[0]}</FormHelperText>}
                            </FormControl>
                            {(seekingValue === 'Domestic undergraduate student' ||
                                seekingValue === 'Parent or guardian') && (
                                <FormControl
                                    fullWidth
                                    required
                                    error={!!errors.education}>
                                    <InputLabel id='education-label'>{educationLabel}</InputLabel>
                                    <Select
                                        labelId='education-label'
                                        id='education'
                                        name='education'
                                        label={educationLabel}
                                        color='secondary'
                                        defaultValue=''>
                                        {filteredEducationOptions.map((option) => (
                                            <MenuItem
                                                key={option}
                                                value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.education && <FormHelperText>{errors.education[0]}</FormHelperText>}
                                </FormControl>
                            )}
                            <TextField
                                required
                                margin='dense'
                                id='password'
                                name='password'
                                label='Password'
                                type='password'
                                fullWidth
                                variant='outlined'
                                autoComplete='new-password'
                                error={!!errors.password}
                                helperText={errors.password?.[0]}
                                color='secondary'
                            />
                            <TextField
                                required
                                margin='dense'
                                id='password_confirmation'
                                name='password_confirmation'
                                label='Confirm password'
                                type='password'
                                fullWidth
                                variant='outlined'
                                autoComplete='new-password'
                                error={!!errors.password_confirmation}
                                helperText={errors.password_confirmation?.[0]}
                                color='secondary'
                            />
                            <FormControlLabel
                                control={<Checkbox name='subscribe' />}
                                color='secondary'
                                label='I would like to receive advice, news and events from Curtin University.'
                            />
                            <FormControl
                                component='fieldset'
                                required
                                error={!!errors.consent}>
                                <div className='mb-2'>
                                    <span className='mr-1 text-[var(--colour-red)]'>*</span>
                                    <span className="font-['Effra'] text-base leading-5 font-bold text-[var(--colour-grey-light)]">
                                        To enter the Open Day competition, please confirm one of the following
                                        statements:
                                    </span>
                                </div>
                                <RadioGroup
                                    aria-label='consent'
                                    color='secondary'
                                    name='consent'>
                                    <FormControlLabel
                                        value='Over 18'
                                        control={<Radio />}
                                        color='secondary'
                                        label={
                                            <>
                                                I am 18 years of age or older and have read the{' '}
                                                {openDayTermsUrl ? (
                                                    <a
                                                        href={openDayTermsUrl}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className="font-['Effra'] text-base leading-5 font-normal text-[var(--colour-gold)] underline">
                                                        Open Day Terms and Conditions
                                                    </a>
                                                ) : (
                                                    'Open Day Terms and Conditions'
                                                )}
                                                .
                                            </>
                                        }
                                    />
                                    <FormControlLabel
                                        value='Under 18'
                                        control={<Radio />}
                                        color='secondary'
                                        label={
                                            <>
                                                I am under the age of 18 but have read the{' '}
                                                {openDayTermsUrl ? (
                                                    <a
                                                        href={openDayTermsUrl}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className="font-['Effra'] text-base leading-5 font-normal text-[var(--colour-gold)] underline">
                                                        Open Day Terms and Conditions
                                                    </a>
                                                ) : (
                                                    'Open Day Terms and Conditions'
                                                )}
                                                {' '}
                                                and obtained my parent or legal guardian&apos;s consent before entering this competition.
                                            </>
                                        }
                                    />
                                </RadioGroup>
                                {errors.consent && <FormHelperText>{errors.consent[0]}</FormHelperText>}
                            </FormControl>
                            <ActionButton
                                onClick={(event) => {
                                    event.preventDefault();
                                    formRef.current?.requestSubmit();
                                }}
                                variant='light'
                                fullWidth={true}
                                disabled={isLoading}>
                                {isLoading ? (
                                    <CircularProgress
                                        size={24}
                                        color='inherit'
                                    />
                                ) : (
                                    'Register'
                                )}
                            </ActionButton>
                            <Stack>
                                <div className='inline-flex items-center gap-3 rounded-lg py-2'>
                                    <div className='justify-left'>
                                        <span className="font-['Effra'] text-base leading-5 font-normal">
                                            Already have an account?{' '}
                                        </span>
                                        <button
                                            className="cursor-pointer font-['Effra'] text-base leading-5 font-normal underline"
                                            onClick={() => setView('login')}>
                                            Sign in
                                        </button>
                                    </div>
                                </div>
                                <div className='leading-4'>
                                    <span className="font-['Effra'] text-xs font-normal text-[var(--colour-grey-light)]">
                                        By registering you agree to receive further communications from Curtin. Personal
                                        information is managed in accordance with Curtin&apos;s{' '}
                                    </span>
                                    <a
                                        href='https://www.curtin.edu.au/about/governance/privacy/'
                                        target='_blank'
                                        className="font-['Effra'] text-xs font-normal text-[var(--colour-gold)] underline">
                                        Privacy Statement
                                    </a>
                                    <span className="font-['Effra'] text-xs font-normal text-[var(--colour-grey-light)]">
                                        .
                                    </span>
                                </div>
                            </Stack>
                        </Stack>
                    </form>
                </>
            )}
        </>
    );
}

function ForgotPassword({ setView }: { setView: (view: View) => void }) {
    const [errors, setErrors] = React.useState<Record<string, string[]>>({});
    const [formError, setFormError] = React.useState<string>('');
    const [success, setSuccess] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const formRef = React.useRef<HTMLFormElement>(null);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const form = event.currentTarget;
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        setErrors({});
        setFormError('');
        setSuccess(false);
        setIsLoading(true);

        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());

        try {
            await wordpressFetch({
                path: '/wp-json/open-day-26/v1/account/forgot-password',
                method: 'POST',
                data,
            });

            // --- GTM TRACKING START ---
            (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];

            (window as any).dataCoreLayer.push({
                'event': 'od_form_submit',
                'form_id': 'forgot-password-form',
                'gtm.formSubmit': true,
                'gtm.elementId': 'forgot-password-form',
                'gtm.elementClasses': formRef.current?.className || '',
                'gtm.elementUrl': window.location.href,
                'gtm.elementTarget': '',
            });
            // --- GTM TRACKING END ---

            setSuccess(true);
        } catch (error) {
            const apiError = error as ApiError;
            if (apiError.data?.status === 422) {
                setErrors(apiError.data.errors);
            } else {
                setFormError(apiError.message || 'An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <Box>
                <Alert severity='success'>
                    If an account with that email exists, a password reset link has been sent.
                </Alert>
                <Stack>
                    <div className='flex w-full justify-between gap-3 rounded-lg py-2'>
                        <div>
                            <span className="font-['Effra'] text-base leading-5 font-normal">Back to </span>
                            <button
                                className="cursor-pointer font-['Effra'] text-base leading-5 font-normal underline"
                                onClick={() => setView('login')}>
                                Login
                            </button>
                        </div>
                    </div>
                </Stack>
            </Box>
        );
    }

    return (
        <>
            <div className='mb-10 inline-flex flex-col items-start justify-start gap-1.5 self-stretch'>
                <div className='inline-flex items-center justify-center gap-[3.14px] bg-[var(--colour-gold)] px-3 py-1'>
                    <div className="justify-center font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase">
                        explore your{' '}
                    </div>
                </div>
                <div className='inline-flex items-center justify-center gap-[3.14px] bg-[var(--colour-gold)] px-3 py-1'>
                    <div className='justify-center'>
                        <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase">
                            uni
                        </span>
                        <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase italic">
                            verse
                        </span>
                        <span className="font-['Effra'] text-3xl leading-10 font-black text-[var(--colour-charcoal-dark)] uppercase">
                            {' '}
                            of possibility
                        </span>
                    </div>
                </div>
            </div>
            <form
                onSubmit={handleSubmit}
                ref={formRef}
                id='forgot-password-form'
                noValidate>
                <Stack spacing={2}>
                    {formError && <Alert severity='error'>{formError}</Alert>}
                    <TextField
                        required
                        autoFocus
                        margin='dense'
                        id='email'
                        name='email'
                        label='Email Address'
                        type='email'
                        fullWidth
                        variant='outlined'
                        autoComplete='email'
                        error={!!errors.email}
                        helperText={errors.email?.[0]}
                        color='secondary'
                    />
                    <ActionButton
                        onClick={(event) => {
                            event.preventDefault();
                            formRef.current?.requestSubmit();
                        }}
                        variant='light'
                        fullWidth={true}
                        disabled={isLoading}>
                        {isLoading ? (
                            <CircularProgress
                                size={24}
                                color='inherit'
                            />
                        ) : (
                            'Reset'
                        )}
                    </ActionButton>
                    <Stack>
                        <div className='flex w-full justify-between gap-3 rounded-lg py-2'>
                            <div>
                                <span className="font-['Effra'] text-base leading-5 font-normal">Back to </span>
                                <button
                                    className="cursor-pointer font-['Effra'] text-base leading-5 font-normal underline"
                                    onClick={() => setView('login')}>
                                    Login
                                </button>
                            </div>
                        </div>
                    </Stack>
                </Stack>
            </form>
        </>
    );
}

export default function Registration() {
    const { state } = useAppState();
    const { setIsRegistrationOpen } = useAppDispatchActions();
    const { user, isRegistrationOpen } = state;
    // const { userMutate } = useApi();
    const [view, setView] = React.useState<View>('login');
    const resetViewTimeoutRef = React.useRef<number | null>(null);

    const handleClose = () => {
        setIsRegistrationOpen(0);
        // Reset to login view after a short delay to allow closing animation
        if (resetViewTimeoutRef.current) {
            clearTimeout(resetViewTimeoutRef.current);
        }
        resetViewTimeoutRef.current = window.setTimeout(() => {
            setView('login');
            resetViewTimeoutRef.current = null;
        }, 300);
    };

    // const logout = async () => {
    //     await wordpressFetch({
    //         path: '/wp-json/open-day-26/v1/account/logout',
    //         method: 'POST',
    //     });

    //     userMutate();
    //     handleClose();
    // };

    const renderContent = () => {
        if (user?.email) {
            return <Success handleClose={handleClose} />;
        }
        switch (view) {
            case 'register':
                return (
                    <Register
                        setView={setView}
                        handleClose={handleClose}
                    />
                );
            case 'forgotPassword':
                return <ForgotPassword setView={setView} />;
            case 'login':
            default:
                return (
                    <Login
                        setView={setView}
                        handleClose={handleClose}
                    />
                );
        }
    };

    function RegistrationErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
        return (
            <Stack
                spacing={2}
                py={2}>
                <Alert severity='error'>
                    <div className='mb-1 font-bold'>Something went wrong</div>
                    <div className='text-sm opacity-90'>
                        {error instanceof Error ? error.message : 'An unknown error occurred'}
                    </div>
                </Alert>
                <ActionButton
                    onClick={resetErrorBoundary}
                    variant='light'>
                    Try again
                </ActionButton>
            </Stack>
        );
    }

    React.useEffect(() => {
        setView(isRegistrationOpen == 1 ? 'register' : 'login');
    }, [isRegistrationOpen]);

    React.useEffect(() => {
        return () => {
            if (resetViewTimeoutRef.current) {
                clearTimeout(resetViewTimeoutRef.current);
                resetViewTimeoutRef.current = null;
            }
        };
    }, []);

    return (
        <Dialog
            open={isRegistrationOpen > 0}
            onClose={handleClose}
            maxWidth='sm'
            fullWidth
            disableRestoreFocus
            sx={{
                '& .MuiDialogContent-root': {
                    padding: { xs: '8px', sm: '24px' },
                },
            }}>
            <IconButton
                aria-label='close'
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}>
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <ErrorBoundary
                    FallbackComponent={RegistrationErrorFallback}
                    onReset={() => {
                        // Reset the state of your app so the error doesn't happen again
                        setView('register');
                    }}>
                    <Box pt={1}>{renderContent()}</Box>
                </ErrorBoundary>
            </DialogContent>
        </Dialog>
    );
}

export const RegisterCard: React.FC<{}> = ({}) => {
    const { setIsRegistrationOpen } = useAppDispatchActions();

    return (
        <div className='inline-flex flex-col items-start justify-start gap-9 md:w-96'>
            <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                <div className='h-[2.55px] w-24 bg-[var(--colour-gold)]' />
                <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                    <div className="justify-center self-stretch font-['Effra'] text-2xl leading-7 font-black text-[var(--colour-charcoal-dark)] uppercase">
                        Register to save events you’re keen on attending
                    </div>
                    <div className="justify-center self-stretch font-['Effra'] text-base leading-5 font-normal text-neutral-950">
                        Plan your day by saving course sessions and events that you’re interested in seeing this Open
                        Day.{' '}
                    </div>
                </div>
            </div>
            <div className='flex flex-col items-start justify-start gap-2'>
                <ActionButton onClick={() => setIsRegistrationOpen(1)}>Register</ActionButton>
                <div className='inline-flex items-center justify-center gap-3 rounded-lg py-2'>
                    <div className='justify-center'>
                        <span className="font-['Effra'] text-base leading-5 font-normal text-[var(--colour-charcoal-dark)]">
                            Already have an account?{' '}
                        </span>
                        <button
                            className="cursor-pointer font-['Effra'] text-base leading-5 font-normal text-[var(--colour-charcoal-dark)] underline"
                            onClick={() => setIsRegistrationOpen(2)}>
                            Sign in
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
